import {
  CircleCheck,
  ExternalLink,
  KeyRound,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { EnvironmentBadge } from "@/components/app-registration-badges";
import {
  ConsentConsequences,
  IT_APPROVALS_PATH,
  PermissionDiff,
} from "@/components/app-registration-permission-change";
import { ScopeList } from "@/components/app-registration-scope-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type AppRegistrationApproval,
  type AppRegistrationRequestDetail,
  useAppRegistrationScopes,
  useApproveAppRegistration,
} from "@/lib/api";
import { type ApiErrorMessage, readApiError } from "@/lib/api-errors";
import {
  diffScopeKeys,
  indexScopes,
  needsAdminConsent,
  privilegedKeys,
} from "@/lib/app-registration-scopes";
import { logger } from "@/lib/logger";

import { CopyField } from "./copy-field";

interface ApproveDialogProps {
  request: AppRegistrationRequestDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApproveDialog({ request, open, onOpenChange }: ApproveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {request && open && (
          // Remount per request so the note, the error and the provisioning
          // result never leak from one decision into the next.
          <ApproveForm key={request.id} request={request} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ApproveFormProps {
  request: AppRegistrationRequestDetail;
  onClose: () => void;
}

function ApproveForm({ request, onClose }: ApproveFormProps) {
  const approve = useApproveAppRegistration();
  // Same query `ScopeList` below reads, deduped by TanStack Query — the
  // consent hint and the list can never disagree about a permission.
  const {
    data: scopes,
    isLoading: catalogueLoading,
    error: catalogueError,
  } = useAppRegistrationScopes();
  const catalogue = useMemo(() => indexScopes(scopes), [scopes]);
  const [note, setNote] = useState("");
  // The privileged keys the approver ticked the box against, not a bare
  // boolean: if the list changes under an open dialog (the catalogue
  // refetched), the tick no longer covers it and has to be given again.
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null);
  const [error, setError] = useState<ApiErrorMessage | null>(null);
  const [result, setResult] = useState<AppRegistrationApproval | null>(null);

  const scopeKeys = request.requested_scope_keys ?? [];
  const isRetry = request.status === "failed";
  const isDecommission = request.request_type === "delete";
  const pending = approve.isPending;
  // An amendment carries the set it was raised against, so the approver is
  // shown the *change* rather than the set that would win. Approving a
  // narrowing without seeing the removals is approving the half of this that
  // the desk cannot finish -- see `ConsentConsequences`.
  const isAmendment = request.request_type === "amend";
  const baseScopeKeys = request.base_scope_keys ?? [];
  const diff = diffScopeKeys(baseScopeKeys, scopeKeys);
  // A change to the project's own sign-in registration: its consent goes
  // through the IT approval queue, not a link this dialog hands out.
  const signIn = request.sign_in_registration ?? false;
  // Scopes that let the app administer identity and access, or act as the
  // user's mailbox. Approving them takes an explicit tick, sent as the keys it
  // was ticked against: the server refuses the approval unless they are
  // exactly the request's privileged set, so a stale or missing catalogue here
  // still cannot wave one through.
  const privileged = privilegedKeys(scopeKeys, catalogue);
  const needsAcknowledgement = privileged.length > 0;
  const privilegedId = privileged.join(" ");
  const acknowledged = acknowledgedFor === privilegedId;
  // Until the catalogue is here, nobody knows whether the tick is needed — so
  // Approve waits for it rather than offer a decision without the checkbox.
  // A request holding no permissions (a decommission) has nothing to look up.
  const catalogueReady = scopeKeys.length === 0 || scopes !== undefined;

  const handleApprove = async () => {
    if (pending || !catalogueReady) {
      return;
    }
    setError(null);
    try {
      const approval = await approve.mutateAsync({
        requestId: request.id,
        projectId: request.project_id,
        note: note.trim() || null,
        acknowledgedScopeKeys: needsAcknowledgement && acknowledged ? privileged : [],
      });
      if (approval) {
        setResult(approval);
      } else {
        onClose();
      }
    } catch (err) {
      logger.error("[app-registrations] approve failed:", err);
      setError(readApiError(err, "Failed to approve the request. Please try again."));
    }
  };

  if (result) {
    return <ApprovalResult approval={result} onClose={onClose} />;
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isDecommission
            ? isRetry
              ? "Retry decommission"
              : "Approve decommission"
            : isRetry
              ? "Retry provisioning"
              : isAmendment
                ? "Approve a permission change"
                : "Approve app registration"}
        </DialogTitle>
        <DialogDescription>
          {isDecommission ? (
            <>
              Deletes <strong className="font-mono">{request.display_name}</strong> from the Norican
              directory. {request.requester_email} asked for it to be taken away.
            </>
          ) : isRetry ? (
            <>
              Resumes provisioning for <strong>{request.display_name}</strong>. The approval already
              stands, so this creates nothing twice.
            </>
          ) : isAmendment ? (
            <>
              Changes the delegated permissions on the existing{" "}
              <strong>{request.display_name}</strong>, requested by {request.requester_email}. The
              application, its client id and its credential are untouched.
            </>
          ) : (
            <>
              Creates a real app registration in the Norican directory for{" "}
              <strong>{request.project_name}</strong>, requested by {request.requester_email}.
            </>
          )}
        </DialogDescription>
        {/* The environment, on the one screen where it decides the blast
            radius. A registration serves exactly one environment and carries
            its own consent, so approving production access must not look
            identical to approving a dev one. */}
        {request.environment && (
          <div className="flex items-center gap-2 pt-1">
            <EnvironmentBadge environment={request.environment} />
            <span className="text-muted-foreground text-xs">
              <code className="font-mono">{request.display_name}</code> &mdash; consent granted here
              covers this environment only.
            </span>
          </div>
        )}
        {signIn && (
          <div className="flex items-center gap-2 pt-1">
            <SignInBadge />
            <span className="text-muted-foreground text-xs">
              The project&apos;s sign-in app registration &mdash; everyone who uses the app signs in
              with it.
            </span>
          </div>
        )}
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div>
            <p className="text-muted-foreground text-xs tracking-wider uppercase">
              {isDecommission ? "Reason" : "Purpose"}
            </p>
            <p className="text-foreground mt-1 text-sm">{request.purpose}</p>
          </div>

          {isDecommission && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
              <p className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
                <Trash2 className="size-4 shrink-0" />
                This deletes real objects
              </p>
              <ul className="mt-1 ml-5 list-disc space-y-1 text-xs text-red-800/90 dark:text-red-300/90">
                <li>
                  The federated credential, the enterprise app and the application go, and the admin
                  consent on it goes with them. The application is soft-deleted &mdash; the
                  directory keeps it restorable for 30 days.
                </li>
                <li>
                  Whatever is configured with{" "}
                  {request.entra_app_id ? (
                    <code className="font-mono">{request.entra_app_id}</code>
                  ) : (
                    "this client id"
                  )}{" "}
                  loses delegated Graph access at once, and Scaffold UI cannot update that
                  deployment for them.
                </li>
              </ul>
            </div>
          )}

          {isAmendment && (
            <>
              <div>
                <p className="text-muted-foreground text-xs tracking-wider uppercase">
                  What this changes
                </p>
                <PermissionDiff base={baseScopeKeys} requested={scopeKeys} className="mt-2" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs tracking-wider uppercase">
                  What it would end up with
                </p>
                <ScopeList scopeKeys={scopeKeys} compact className="mt-2" />
              </div>
              {/* Before the decision, not after it. This is the part of the
                  change that approving does *not* do. */}
              <ConsentConsequences
                added={diff.added}
                removed={diff.removed}
                consentUrl={request.admin_consent_url}
                signInRegistration={signIn}
                linkToItApprovals
              />
            </>
          )}

          {!isDecommission && !isAmendment && (
            <>
              <div>
                <p className="text-muted-foreground text-xs tracking-wider uppercase">
                  Permissions being granted
                </p>
                <ScopeList scopeKeys={scopeKeys} compact className="mt-2" />
              </div>

              {needsAdminConsent(scopeKeys, catalogue) && (
                <p className="text-muted-foreground flex items-start gap-2 text-xs">
                  <ShieldAlert className="mt-0.5 size-3.5 shrink-0" />
                  At least one permission needs a directory admin to consent. Approving creates the
                  app; the permissions stay inert until someone follows the consent link this
                  returns.
                </p>
              )}
            </>
          )}

          {needsAcknowledgement && (
            <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
              <p className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
                <KeyRound className="size-4 shrink-0" />
                Privileged access
              </p>
              <p className="text-xs text-red-800/90 dark:text-red-300/90">
                {privileged.map((key) => catalogue.get(key)?.value ?? key).join(", ")}{" "}
                {privileged.length === 1 ? "lets" : "let"} this app, as whoever signs in to it,
                manage who has access to what &mdash; give out permissions or roles, change group or
                team membership, change accounts and how people sign in, or manage devices &mdash;
                read secrets such as device passwords and recovery keys, or act as their mailbox:
                send mail as them, forward it, or open it to others. If an administrator uses the
                app, so can the app.
              </p>
              <div className="flex items-start gap-2 pt-1">
                <Checkbox
                  id="approve-privileged"
                  checked={acknowledged}
                  onCheckedChange={(value) =>
                    setAcknowledgedFor(value === true ? privilegedId : null)
                  }
                  disabled={pending}
                  className="mt-0.5"
                />
                <Label htmlFor="approve-privileged" className="text-sm font-normal">
                  I&apos;ve checked why this app needs privileged access.
                </Label>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="approve-note">Decision note (optional)</Label>
            <Textarea
              id="approve-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the requester or the next approver should know."
              rows={3}
              disabled={pending}
            />
          </div>

          {!catalogueReady && (
            <p className="text-muted-foreground flex items-start gap-2 text-xs">
              {catalogueLoading ? (
                <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin" />
              ) : (
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              )}
              {catalogueError
                ? "Couldn't load the permission catalogue, so this can't be approved yet. Close the dialog and reopen it to retry."
                : "Loading the permission catalogue…"}
            </p>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
              <p className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
                <TriangleAlert className="size-4 shrink-0" />
                {error.message}
              </p>
              {error.detail && (
                <pre className="text-foreground bg-background/70 mt-2 overflow-x-auto rounded p-2 text-xs whitespace-pre-wrap">
                  {error.detail}
                </pre>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            disabled={pending || !catalogueReady || (needsAcknowledgement && !acknowledged)}
            variant={isDecommission ? "destructive" : "default"}
          >
            {pending && <Loader2 className="animate-spin" />}
            {isDecommission
              ? pending
                ? "Deleting from Entra…"
                : isRetry
                  ? "Retry decommission"
                  : "Approve & delete"
              : pending
                ? isAmendment
                  ? "Applying in Entra…"
                  : "Creating in Entra…"
                : isRetry
                  ? "Retry provisioning"
                  : isAmendment
                    ? "Approve & apply"
                    : "Approve & provision"}
          </Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}

/**
 * What the approve call created.
 *
 * The consent link is the headline: a provisioned app's permissions do not work
 * until a directory admin follows it, so an approver who closes this screen
 * without using it has done half the job.
 */
function ApprovalResult({
  approval,
  onClose,
}: {
  approval: AppRegistrationApproval;
  onClose: () => void;
}) {
  const { request } = approval;
  const wasAmendment = request.request_type === "amend";
  const signIn = request.sign_in_registration ?? false;
  const warnings = approval.consent_warnings ?? [];
  const added = approval.added_scope_keys ?? [];

  if (approval.decommission) {
    return (
      <DecommissionResult
        approval={approval}
        decommission={approval.decommission}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {/* No green tick on an amendment. It is not finished: the added
              permissions are inert until someone re-consents and the removed
              ones are still granted, and a tick next to that is the single
              most misleading thing this screen could show. */}
          {wasAmendment ? (
            <ShieldAlert className="size-5 text-amber-600 dark:text-amber-400" />
          ) : (
            <CircleCheck className="size-5 text-green-600 dark:text-green-400" />
          )}
          {wasAmendment ? "Permissions changed — consent has not" : "App registration created"}
          {signIn && <SignInBadge />}
        </DialogTitle>
        <DialogDescription>
          {wasAmendment
            ? `${request.display_name} now lists the approved permissions.`
            : `${request.display_name} now exists in the Norican directory.`}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          {wasAmendment && (
            <>
              <PermissionDiff
                base={request.base_scope_keys ?? []}
                requested={request.requested_scope_keys ?? []}
              />
              {warnings.length > 0 && (
                <div className="border-border space-y-2 rounded-md border p-3">
                  <p className="text-foreground text-sm font-medium">
                    What approving this has <em>not</em> done
                  </p>
                  <ul className="text-muted-foreground space-y-2 text-xs">
                    {warnings.map((warning) => (
                      <li key={warning} className="flex items-start gap-2">
                        <ShieldAlert className="mt-0.5 size-3.5 shrink-0" />
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* `next_step` used to be this box's heading, which meant it was
                  shown only when there were warnings to hang it under -- and
                  duplicated the copy inside the consent panel below whenever
                  there were. The panel is where it belongs; this renders it
                  only in the one case that panel does not exist, so the
                  sentence saying the job is not finished can never be the
                  thing that disappears. */}
              {/* A sign-in registration's next step is the IT handoff below,
                  so the generic "finish the parts this desk cannot do" is left out. */}
              {!signIn && !request.admin_consent_url && (
                <p className="text-foreground text-sm font-medium">{approval.next_step}</p>
              )}
            </>
          )}

          {signIn && added.length > 0 && <SignInConsentHandoff />}

          {!signIn && request.admin_consent_url && (
            <div className="border-accent bg-accent/5 rounded-md border p-3">
              <p className="text-foreground text-sm font-medium">Grant admin consent</p>
              <p className="text-muted-foreground mt-1 text-xs">{approval.next_step}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button asChild>
                  <a href={request.admin_consent_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink />
                    Open consent link
                  </a>
                </Button>
              </div>
              <CopyField label="Consent URL" value={request.admin_consent_url} className="mt-3" />
            </div>
          )}

          {/* Where the one-time client secret used to be. Nothing here is
              secret: the application authenticates as a managed identity the
              project already owns, so there is no value to copy down before
              this dialog closes, and no secure channel to find. */}
          {(approval.redirect_uris?.length ?? 0) > 0 && (
            <div className="border-accent bg-accent/5 rounded-md border p-3">
              <p className="text-foreground text-sm font-medium">Already wired up</p>
              <p className="text-muted-foreground mt-1 text-xs">
                The app signs users in against this registration directly and receives the code at
                this callback &mdash; registered for you, for this environment.
              </p>
              <div className="mt-2 space-y-2">
                {approval.redirect_uris?.map((uri) => (
                  <CopyField key={uri} label="Redirect URI" value={uri} />
                ))}
              </div>
              {(approval.federated_identities?.length ?? 0) > 0 && (
                <p className="text-muted-foreground mt-2 text-xs">
                  Authenticating as{" "}
                  <span className="text-foreground font-mono">
                    {approval.federated_identities?.join(", ")}
                  </span>
                  . No client secret was created.
                </p>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {request.entra_app_id && (
              <CopyField label="Application (client) ID" value={request.entra_app_id} />
            )}
            {request.entra_object_id && (
              <CopyField label="Object ID" value={request.entra_object_id} />
            )}
            {request.entra_sp_object_id && (
              <CopyField label="Service principal object ID" value={request.entra_sp_object_id} />
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}

/** Marks a row that changes the project's own sign-in app registration. */
function SignInBadge() {
  return (
    <Badge variant="secondary" title="Everyone who uses the app signs in with this registration">
      Sign-in
    </Badge>
  );
}

/**
 * The way to a sign-in registration's next step: the IT approvals page.
 *
 * The server's consent warning above already says why the added permissions
 * wait for IT (Entra does not extend a grant when the permission list grows),
 * so this carries only what that sentence does not: the link, and that
 * sign-in itself is unaffected meanwhile.
 */
function SignInConsentHandoff() {
  return (
    <div className="border-accent bg-accent/5 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
      <div>
        <p className="text-foreground text-sm font-medium">Next: IT approval</p>
        <p className="text-muted-foreground mt-1 text-xs">
          Sign-in keeps working on what IT already approved meanwhile.
        </p>
      </div>
      <Button asChild variant="outline">
        <Link to={IT_APPROVALS_PATH}>
          <ShieldCheck />
          Open IT approvals
        </Link>
      </Button>
    </div>
  );
}

/**
 * What an approved decommission removed.
 *
 * The headline is deliberately the thing that is *not* finished. Deleting the
 * application is the easy half; the project&apos;s own deployment still holds
 * the client id as `GRAPH_CLIENT_ID`, Scaffold UI has never been able to write
 * or clear it, and nothing will tell the requester their Graph calls have
 * stopped except the Graph calls stopping. `next_step` says so, and it is
 * given the same prominence the consent link gets on the way up.
 *
 * `manual_follow_up` is the other half of the honesty: the desk refuses to
 * delete anything it cannot prove it created, so anything it left behind is
 * listed here rather than quietly omitted.
 */
function DecommissionResult({
  approval,
  decommission,
  onClose,
}: {
  approval: AppRegistrationApproval;
  decommission: NonNullable<AppRegistrationApproval["decommission"]>;
  onClose: () => void;
}) {
  // Optional in the schema (it has a server-side default), so the one place
  // that iterates it defaults rather than asserting.
  const followUp = decommission.manual_follow_up ?? [];
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Trash2 className="size-5" />
          App registration decommissioned
        </DialogTitle>
        <DialogDescription>
          {approval.request.display_name} has been removed from the Norican directory.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
              <TriangleAlert className="size-4 shrink-0" />
              The project&apos;s deployment still has to be updated by a human
            </p>
            <p className="mt-1 text-xs text-amber-900/90 dark:text-amber-200/90">
              {approval.next_step}
            </p>
          </div>

          <div className="text-muted-foreground space-y-1 text-sm">
            {decommission.already_absent ? (
              <p>The directory already held nothing for this registration.</p>
            ) : (
              <p>
                Deleted {decommission.federated_credentials_deleted} federated credential
                {decommission.federated_credentials_deleted === 1 ? "" : "s"}
                {decommission.service_principal_deleted ? ", the enterprise app" : ""} and the
                application.
              </p>
            )}
            <p className="text-xs">
              The application is soft-deleted &mdash; the directory keeps it restorable for 30 days
              before removing it for good.
            </p>
          </div>

          {followUp.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Left for a human, on purpose
              </p>
              <ul className="mt-1 ml-5 list-disc space-y-1 text-xs text-red-800/90 dark:text-red-300/90">
                {followUp.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <CopyField label="Deleted application (client) ID" value={decommission.app_id} />
        </div>

        <DialogFooter className="mt-6">
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}
